import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/get-the-look")({
  head: () => ({
    meta: [
      { title: "Find Any Outfit — Shop It At Your Budget | Like2Fit" },
      {
        name: "description",
        content:
          "Upload any outfit photo from Pinterest, Instagram or TikTok and find similar items at your budget — online or in stores near you. Free to try.",
      },
      { property: "og:title", content: "Find Any Outfit — Shop It At Your Budget | Like2Fit" },
      {
        property: "og:description",
        content:
          "Upload any outfit photo from Pinterest, Instagram or TikTok and find similar items at your budget — online or in stores near you. Free to try.",
      },
      { property: "og:image", content: "/icons/icon-512.png" },
      { property: "og:url", content: "https://like2fit.com/get-the-look" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://like2fit.com/get-the-look" }],
  }),
  component: GetTheLookCampaign,
});

const INK = "#0A0A0A";
const CORAL = "#FF5C5C";
const TINT = "#FAECE7";
const MUTED = "rgba(10,10,10,0.62)";
const SERIF = "'Instrument Serif', 'Times New Roman', Georgia, serif";
const SANS = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function preserveUtmAndGo(campaign: "get-the-look" | "wardrobe") {
  try {
    const params = new URLSearchParams(window.location.search);
    sessionStorage.setItem("l2f_utm_source", params.get("utm_source") ?? "");
    sessionStorage.setItem("l2f_utm_medium", params.get("utm_medium") ?? "");
    sessionStorage.setItem("l2f_utm_campaign", params.get("utm_campaign") ?? "");
    sessionStorage.setItem("l2f_campaign_entry", campaign);
  } catch {
    /* ignore storage errors */
  }
  window.location.href = "/";
}

function GetTheLookCampaign() {
  // Load magazine fonts once for this standalone page.
  useEffect(() => {
    const id = "l2f-campaign-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const onCTA = () => preserveUtmAndGo("get-the-look");

  return (
    <div style={{ minHeight: "100vh", background: "#fff", color: INK, fontFamily: SANS }}>
      {/* Header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: "0.5px solid rgba(10,10,10,0.05)",
        }}
      >
        <span style={{ color: CORAL, fontWeight: 600, fontSize: 20, letterSpacing: "-0.5px" }}>
          Like2Fit
        </span>
        <Link
          to="/"
          style={{
            fontSize: 13,
            color: "rgba(10,10,10,0.7)",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Sign in
        </Link>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "0 24px" }}>
        {/* Hero */}
        <section style={{ padding: "56px 0 48px" }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.25em",
              color: CORAL,
              margin: "0 0 20px",
            }}
          >
            Like2Fit · Get The Look
          </p>
          <h1
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 48,
              lineHeight: 1.05,
              letterSpacing: "-0.5px",
              margin: 0,
              color: INK,
            }}
          >
            Those outfits you saved. Now find out where to buy them.
          </h1>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.6, color: MUTED }}>
            Upload any photo from Pinterest, Instagram or TikTok and we'll find similar
            items at your budget — from stores near you or online.
          </p>

          <button onClick={onCTA} style={primaryBtn}>
            Upload an outfit photo →
          </button>
          <p style={disclaimer}>Free to try · No account needed</p>
        </section>

        {/* Three steps */}
        <section style={{ padding: "32px 0 56px" }}>
          <p style={eyebrow}>How it works</p>
          <h2 style={sectionHeading}>Three taps. One outfit.</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 32 }}>
            <Step
              n="01"
              icon="📸"
              title="Upload"
              body="Screenshot any outfit you love — saved posts, Pinterest pins, TikTok stills."
            />
            <Step
              n="02"
              icon="✨"
              title="Discover"
              body="We scan the look and surface similar items within your budget — across hundreds of retailers."
            />
            <Step
              n="03"
              icon="📍"
              title="Shop"
              body="Buy online or find the items waiting in physical stores near you."
            />
          </div>
        </section>

        {/* In-store hook */}
        <section
          style={{
            background: TINT,
            borderRadius: 24,
            padding: "40px 28px",
            marginBottom: 48,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 26,
              marginBottom: 20,
            }}
            aria-hidden="true"
          >
            📍
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 36,
              lineHeight: 1.1,
              letterSpacing: "-0.3px",
              margin: "0 0 16px",
              color: INK,
            }}
          >
            In stores near you. Right now.
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(10,10,10,0.75)", margin: 0 }}>
            Unlike other fashion apps, Like2Fit finds items available in physical stores near your
            location — so you can try before you buy.
          </p>
        </section>

        {/* Budget hook */}
        <section style={{ padding: "16px 0 64px" }}>
          <p style={eyebrow}>Your wallet, respected</p>
          <h2 style={sectionHeading}>Set your budget. We do the rest.</h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, marginTop: 16 }}>
            Tell us what you want to spend and we'll only show you items within that range. No more
            falling in love with outfits you can't afford.
          </p>
        </section>

        {/* Final CTA */}
        <section
          style={{
            background: INK,
            color: "#fff",
            borderRadius: 24,
            padding: "56px 28px",
            textAlign: "center",
            margin: "0 0 56px",
          }}
        >
          <p
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 34,
              lineHeight: 1.1,
              margin: "0 0 28px",
            }}
          >
            Stop saving. Start wearing.
          </p>
          <button onClick={onCTA} style={{ ...primaryBtn, marginTop: 0 }}>
            Upload an outfit photo →
          </button>
          <p style={{ ...disclaimer, color: "rgba(255,255,255,0.6)" }}>
            Free to try · No sign up needed
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer
        style={{
          padding: "32px 24px 48px",
          borderTop: "0.5px solid rgba(10,10,10,0.06)",
          textAlign: "center",
        }}
      >
        <p style={{ color: CORAL, fontWeight: 600, fontSize: 18, margin: "0 0 12px" }}>Like2Fit</p>
        <nav
          aria-label="Footer"
          style={{ display: "flex", gap: 18, justifyContent: "center", fontSize: 13 }}
        >
          <Link to="/privacy" style={footerLink}>
            Privacy
          </Link>
          <Link to="/terms" style={footerLink}>
            Terms
          </Link>
          <Link to="/" style={footerLink}>
            Back to home
          </Link>
        </nav>
      </footer>
    </div>
  );
}

function Step({ n, icon, title, body }: { n: string; icon: string; title: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
      <div
        style={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: TINT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            color: CORAL,
            margin: "0 0 6px",
          }}
        >
          {n}
        </p>
        <h3 style={{ fontSize: 19, fontWeight: 700, margin: "0 0 6px", color: INK }}>{title}</h3>
        <p style={{ fontSize: 15, lineHeight: 1.55, color: MUTED, margin: 0 }}>{body}</p>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 32,
  padding: "18px 24px",
  background: CORAL,
  color: "#fff",
  border: "none",
  borderRadius: 999,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  letterSpacing: "-0.1px",
};

const disclaimer: React.CSSProperties = {
  marginTop: 14,
  fontSize: 13,
  color: "rgba(10,10,10,0.5)",
  textAlign: "center",
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.25em",
  color: CORAL,
  margin: "0 0 12px",
};

const sectionHeading: React.CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontSize: 36,
  lineHeight: 1.1,
  letterSpacing: "-0.3px",
  margin: 0,
  color: INK,
};

const footerLink: React.CSSProperties = {
  color: "rgba(10,10,10,0.7)",
  textDecoration: "none",
};
